import type { SupabaseClient } from '@supabase/supabase-js';

/** A caught edge-function error with the response status and parsed JSON
 * body attached, when supabase-js could recover them (FunctionsHttpError's
 * `context` is the raw Response). Mirrors the `error.context.json()`
 * unwrapping pattern `frontend/src/lib/data/ops.ts` uses for the mobile
 * approvals screen (plan L358-361) — the portal needs the same care so a
 * `409`/`403` with a machine-readable `error` code (e.g.
 * `mfa_enrollment_required`) can drive UI branching instead of being
 * flattened into a generic failure message. */
export class EdgeFunctionError extends Error {
  status: number;
  code: string | null;
  body: Record<string, unknown>;

  constructor(message: string, status: number, code: string | null, body: Record<string, unknown>) {
    super(message);
    this.name = 'EdgeFunctionError';
    this.status = status;
    this.code = code;
    this.body = body;
  }

  /** `.message` is the machine CODE (`url_domain_not_allowed`,
   * `invalid_rail`, ...) — useful for `if (err.code === ...)` branching, but
   * wrong to show a host. Found live in a Task 18 review-round browser
   * smoke: a rejected pay-rail URL rendered "url_domain_not_allowed" instead
   * of the server's actual host-readable text. Most of this repo's edge
   * functions put that text in the JSON body's `message` field (host-
   * pay-rail, rail-otp, ...); this falls back to `.message` only when the
   * body doesn't carry one. */
  get friendlyMessage(): string {
    return typeof this.body.message === 'string' ? this.body.message : this.message;
  }
}

export async function callEdgeFunction<T>(
  supabase: SupabaseClient,
  name: string,
  body?: Record<string, unknown>,
): Promise<T> {
  const { data, error } = await supabase.functions.invoke(name, { body: body ?? {} });
  if (error) {
    let status = 500;
    let parsedBody: Record<string, unknown> = {};
    const context = (error as { context?: Response }).context;
    if (context && typeof context.json === 'function') {
      status = context.status;
      try {
        parsedBody = await context.json();
      } catch {
        /* non-JSON error body — fall through with an empty object */
      }
    }
    const code = typeof parsedBody.error === 'string' ? parsedBody.error : null;
    throw new EdgeFunctionError((code ?? error.message) as string, status, code, parsedBody);
  }
  return data as T;
}
